import { useEffect, useRef, useState } from 'react';

import { cn } from '@utils/cn';

import { Button } from '@ui-component-library/components/Button';
import { Icon } from '@ui-component-library/components/Icon';
import { useDebounce } from '@ui-component-library/hooks/useDebounce';

import type { TextareaProps } from './Textarea.types';

import {
  clearButtonClasses,
  textareaClasses,
  textareaExtraClasses,
  wrapperClasses
} from './Textarea.styles';

function Textarea({
  name,
  className,
  value,
  isValid = undefined,

  placeholder,
  disabled = false,
  onChange,
  debounceDelay = 300,
  rows = 3,
  ...props
}: TextareaProps) {
  const [inputValue, setInputValue] = useState(value ?? '');
  const [isFocused, setIsFocused] = useState(Boolean(value));
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const debouncedInputValue = useDebounce(inputValue, debounceDelay);

  useEffect(() => {
    onChange(debouncedInputValue);
  }, [debouncedInputValue, onChange]);

  useEffect(() => {
    setTimeout(() => {
      setInputValue(value ?? '');
    }, 0);
  }, [value]);

  const handleChange = ({ currentTarget }: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(currentTarget.value);
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!inputValue && !wrapperRef.current?.contains(e.relatedTarget)) {
      setIsFocused(false);
    }
  };

  const clearInput = () => {
    setInputValue('');
  };

  const shouldShowValidation = isValid !== undefined;
  const showSuccessIcon = shouldShowValidation && isValid === true;
  const showErrorIcon = shouldShowValidation && isValid === false;

  return (
    <div
      ref={wrapperRef}
      className={cn(
        wrapperClasses({
          validationStatus: isValid,
          disabled: disabled
        }),
        className
      )}
      onClick={() => {
        inputRef.current?.focus();
      }}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      <textarea
        ref={inputRef}
        name={name}
        placeholder={placeholder ?? ''}
        value={inputValue}
        className={cn(textareaClasses({ isFocused }), className)}
        onChange={handleChange}
        {...props}
        rows={rows}
      />
      {isFocused && (
        <Button
          variant="tertiary"
          size="icon"
          className={cn(clearButtonClasses({ hasInputValue: Boolean(inputValue) }))}
          onClick={clearInput}
        >
          <Icon name="CrossFilled" size="large" />
        </Button>
      )}

      {showSuccessIcon && (
        <Icon
          name="CheckFilled"
          className={cn('fill-success-900', textareaExtraClasses({ isFocused }))}
          size="large"
        />
      )}

      {showErrorIcon && (
        <Icon
          name="ExclamationFilled"
          className={cn('fill-danger-500', textareaExtraClasses({ isFocused }))}
          size="large"
        />
      )}
    </div>
  );
}

export { Textarea };
